/**
 * Split Maestro spent hours into legend stacks.
 *
 * Spent does not carry P6 resource assignments. It carries an operation code
 * (activity_code) plus a discipline. Those map through the Accounting coding
 * structure (operation-code ranges and the catalog) onto the same legend
 * choices the histogram already uses, plus the spend-only columns.
 */

import { COST_TYPES, DISCIPLINES, OPERATION_CODES } from '../data/codingStructureData';
import { DISC_LABELS } from './evmLegend';

/** Maestro / coding-structure acronym → portal discipline id used by the legend. */
const PORTAL_FROM_MAESTRO = {
  PIP: 'piping',
  MWR: 'mechanical',
  ELE: 'electrical',
  SHM: 'ducts',
  PFS: 'piping',
  CIV: 'civil',
  STR: 'structural',
  TNK: 'boilermaker',
  BMT: 'boilermaker',
  IN: 'indirect',
  IND: 'indirect'
};

const SHOP_CODES = new Set(['PFS', 'TNK', 'PF']);

/** Direct-labour and extras ranges from the coding structure. */
const CODE_RANGES = [
  { from: 21000, to: 21999, code: 'PIP' },
  { from: 22000, to: 22999, code: 'MWR' },
  { from: 23000, to: 23999, code: 'ELE' },
  { from: 24000, to: 24999, code: 'SHM' },
  { from: 25000, to: 25999, code: 'PFS' },
  { from: 26000, to: 26999, code: 'CIV' },
  { from: 27000, to: 27999, code: 'STR' },
  { from: 28000, to: 28999, code: 'TNK' },
  { from: 29000, to: 29999, code: 'BMT' },
  { from: 81000, to: 81999, code: 'PIP' },
  { from: 82000, to: 82999, code: 'MWR' },
  { from: 83000, to: 83999, code: 'ELE' },
  { from: 84000, to: 84999, code: 'SHM' },
  { from: 85000, to: 85999, code: 'PFS' },
  { from: 86000, to: 86999, code: 'CIV' },
  { from: 87000, to: 87999, code: 'STR' },
  { from: 88000, to: 88999, code: 'TNK' },
  { from: 89000, to: 89999, code: 'BMT' }
];

const CATALOG_BY_CODE = new Map(OPERATION_CODES.map((c) => [c.code, c]));

export const ACCT_LEGEND_FIELDS = [
  { id: 'op_code', label: 'Op Code' },
  { id: 'op_desc', label: 'Op Description' },
  { id: 'group', label: 'Coding group' },
  { id: 'cost_type', label: 'Cost type' }
];

const SKIP_SLICER_COLS = new Set([
  'id', 'project_id', 'date', 'reg', 't12', 'dbl', 'hours', 'qty',
  'created_at', 'updated_at', 'deleted_at', 'imported_at'
]);

export function normOpCode(code) {
  const s = String(code ?? '').trim();
  if (!s) return '';
  if (/^\d+$/.test(s)) return s.padStart(5, '0');
  return s;
}

function rangeDiscipline(opCode) {
  const n = Number(opCode);
  if (!Number.isFinite(n)) return null;
  const hit = CODE_RANGES.find((r) => n >= r.from && n <= r.to);
  if (hit) return hit.code;
  if (n >= 1000 && n < 21000) return 'IN';
  return null;
}

function portalDiscipline(maestroCode) {
  const c = String(maestroCode || '').trim().toUpperCase();
  if (!c || c === 'UNK') return 'unknown';
  if (PORTAL_FROM_MAESTRO[c]) return PORTAL_FROM_MAESTRO[c];
  const named = DISCIPLINES.find((d) => d.id === c);
  if (named && PORTAL_FROM_MAESTRO[named.id]) return PORTAL_FROM_MAESTRO[named.id];
  return 'unknown';
}

export function classifySpendRow(row = {}) {
  const opCode = normOpCode(row.activity_code);
  const catalog = CATALOG_BY_CODE.get(opCode) || null;
  const fromRow = String(row.discipline || '').trim().toUpperCase();
  const maestroDiscipline = (fromRow && fromRow !== 'UNK' ? fromRow : null)
    || catalog?.discipline
    || rangeDiscipline(opCode)
    || 'IN';
  const costLetter = catalog?.costType || '';
  const description = (catalog?.description && !/^1st operation code$/i.test(catalog.description))
    ? catalog.description
    : (String(row.activity_name || '').trim() || catalog?.description || '');
  return {
    opCode: opCode || '(no value)',
    description: description || opCode || '(no value)',
    group: catalog?.group || '(no value)',
    costType: COST_TYPES[costLetter]?.name || (costLetter ? costLetter : '(no value)'),
    maestroDiscipline,
    portalDiscipline: portalDiscipline(maestroDiscipline),
    shopOrField: SHOP_CODES.has(maestroDiscipline) ? 'shop' : 'field'
  };
}

/** Spend can follow this legend. Project and P6 UDFs / resource codes cannot. */
export function spentCanSplit(legendBy) {
  const v = String(legendBy || '');
  if (!v || v === 'total' || v === 'project') return false;
  if (v.startsWith('tt:') || v.startsWith('acct:')) return true;
  if (v === 'discipline' || v === 'resource') return true;
  if (v === 'p6:rsrc_id' || v === 'p6:rsrc_name') return true;
  if (v === 'p6:activity_code' || v === 'p6:activity_name') return true;
  return false;
}

function resourceLabel(info) {
  const generic = !info.description || info.description === info.opCode
    || /^1st operation code$/i.test(info.description);
  if (!generic && info.description !== '(no value)') return info.description;
  const disc = DISC_LABELS[info.portalDiscipline] || info.maestroDiscipline || 'Other';
  return info.opCode && info.opCode !== '(no value)' ? `${disc} · ${info.opCode}` : disc;
}

function identityFor(info, legendBy) {
  if (legendBy === 'discipline') {
    const id = info.portalDiscipline || 'unknown';
    return { discipline: id, disciplineLabel: DISC_LABELS[id] || id };
  }
  if (legendBy === 'resource' || legendBy === 'p6:rsrc_id' || legendBy === 'p6:rsrc_name') {
    const label = resourceLabel(info);
    return {
      resourceId: label,
      resourceName: label,
      resourceKey: label,
      legendId: label,
      legendLabel: label,
      discipline: info.portalDiscipline
    };
  }
  if (legendBy === 'p6:activity_code' || legendBy === 'acct:op_code') {
    return { legendId: info.opCode, legendLabel: info.opCode, discipline: info.portalDiscipline };
  }
  if (legendBy === 'p6:activity_name' || legendBy === 'acct:op_desc') {
    return { legendId: info.description, legendLabel: info.description, discipline: info.portalDiscipline };
  }
  if (legendBy === 'acct:group') {
    return { legendId: info.group, legendLabel: info.group, discipline: 'unknown' };
  }
  if (legendBy === 'acct:cost_type') {
    return { legendId: info.costType, legendLabel: info.costType, discipline: 'unknown' };
  }
  return null;
}

/**
 * Maestro histogram rows → a byResource curve the existing stack collector
 * can colour. Time Tracker column legends come from the API instead.
 */
export function spentCurveFromRows(rows, legendBy) {
  if (!spentCanSplit(legendBy) || String(legendBy).startsWith('tt:')) return null;
  const byKey = new Map();
  (rows || []).forEach((row) => {
    const info = classifySpendRow(row);
    const ident = identityFor(info, legendBy);
    if (!ident) return;
    const key = ident.legendId || ident.resourceId || ident.discipline || 'other';
    if (!byKey.has(key)) {
      byKey.set(key, {
        ...ident,
        resourceId: ident.resourceId || key,
        resourceKey: ident.resourceKey || key,
        shopOrField: info.shopOrField,
        weeks: {}
      });
    }
    const bucket = byKey.get(key);
    const week = String(row.week_start || '').slice(0, 10);
    if (!week) return;
    bucket.weeks[week] = (bucket.weeks[week] || 0) + (Number(row.hours) || 0);
  });
  if (!byKey.size) return null;
  const byResource = {};
  byKey.forEach((bucket, key) => {
    const series = Object.keys(bucket.weeks).sort().map((date) => ({
      date,
      period: Math.round((bucket.weeks[date] + Number.EPSILON) * 100) / 100
    }));
    byResource[key] = { ...bucket, series, dailySeries: series };
  });
  return { byResource };
}

/**
 * Paint the existing bar total, split by raw weights. Returns null when this
 * week has no weights — the caller keeps a single colour for that week.
 */
export function apportionSlices(target, slices) {
  const t = Math.round(((Number(target) || 0) + Number.EPSILON) * 100) / 100;
  const positive = (slices || []).filter((s) => (Number(s.raw) || 0) > 0);
  if (!(t > 0) || !positive.length) return null;
  const rawSum = positive.reduce((s, p) => s + Number(p.raw), 0);
  if (!(rawSum > 0)) return null;
  let used = 0;
  return positive.map((p, i) => {
    const value = i === positive.length - 1
      ? Math.round((t - used) * 100) / 100
      : Math.round((t * (Number(p.raw) / rawSum)) * 100) / 100;
    used = Math.round((used + value) * 100) / 100;
    return { key: p.key, value: Math.max(0, value) };
  });
}

const MEASURE_TYPE = /date|time|int|numeric|double|real|bool|bytea|json/i;

/** Slicer columns that are categories, so they can be a legend. */
export function columnLegendFields(slicerFields) {
  return (slicerFields || []).filter((f) => {
    const col = String(f.column || f.id || '').toLowerCase();
    if (!col || SKIP_SLICER_COLS.has(col)) return false;
    if (MEASURE_TYPE.test(String(f.dataType || ''))) return false;
    return true;
  });
}
